import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
/**
 * A banking-app PIN pad: six dots and big round number keys (the Face ID key bottom left when the device has one).
 * Works with a hardware keyboard too. Calls onComplete with the six digits; the parent clears it with `resetKey`
 * after a wrong PIN, and the dots give a little shake.
 */
export function PinPad({ onComplete, busy, resetKey, label = "Enter your PIN", onBiometric, biometricLabel }) {
    const [pin, setPin] = useState("");
    const [shake, setShake] = useState(false);
    const first = useRef(true);
    useEffect(() => {
        setPin("");
        if (first.current) {
            first.current = false;
            return;
        }
        setShake(true);
        const t = window.setTimeout(() => setShake(false), 480);
        return () => window.clearTimeout(t);
    }, [resetKey]);
    // Keys answer the moment a finger touches (pointerdown, not click, which waits for the finger to lift), and the
    // sixth digit unlocks straight away (one frame to show the last dot).
    const pinRef = useRef("");
    pinRef.current = pin;
    const press = (d) => {
        if (busy)
            return;
        const cur = pinRef.current;
        const next = d === "del" ? cur.slice(0, -1) : cur.length < 6 ? cur + d : cur;
        pinRef.current = next;
        setPin(next);
        if (next.length === 6 && cur.length === 5)
            requestAnimationFrame(() => onComplete(next));
    };
    const tap = (d) => (e) => { if (e.button !== 0)
        return; e.preventDefault(); press(d); };
    useEffect(() => {
        const on = (e) => { if (/^\d$/.test(e.key))
            press(e.key);
        else if (e.key === "Backspace")
            press("del"); };
        window.addEventListener("keydown", on);
        return () => window.removeEventListener("keydown", on);
    });
    return (_jsxs("div", { className: "tc-pin", "aria-label": label, children: [_jsx("div", { className: "tc-pin-label", children: label }), _jsx("div", { className: `tc-pin-dots${busy ? " busy" : ""}${shake ? " shake" : ""}`, "aria-live": "polite", "aria-label": `${pin.length} of 6 digits entered`, children: Array.from({ length: 6 }, (_, i) => _jsx("i", { className: i < pin.length ? "on" : "" }, i)) }), _jsx("div", { className: "tc-pin-keys", children: ["1", "2", "3", "4", "5", "6", "7", "8", "9", "bio", "0", "del"].map((k, i) => {
                    if (k === "bio")
                        return onBiometric
                            ? _jsx("button", { type: "button", className: "tc-pin-key fn", onClick: onBiometric, "aria-label": biometricLabel ?? "Use Face ID", disabled: busy, children: _jsx(FaceId, {}) }, i)
                            : _jsx("span", {}, i);
                    return (_jsx("button", { type: "button", className: `tc-pin-key${k === "del" ? " fn" : ""}`, onPointerDown: tap(k), onClick: (e) => { if (e.detail === 0)
                            press(k); }, "aria-label": k === "del" ? "Delete" : k, disabled: busy || (k === "del" && !pin), children: k === "del" ? _jsx(Backspace, {}) : k }, i));
                }) })] }));
}
const FaceId = () => (_jsxs("svg", { width: "30", height: "30", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "1.7", strokeLinecap: "round", "aria-hidden": "true", children: [_jsx("path", { d: "M3 8V5.5A2.5 2.5 0 0 1 5.5 3H8M16 3h2.5A2.5 2.5 0 0 1 21 5.5V8M21 16v2.5a2.5 2.5 0 0 1-2.5 2.5H16M8 21H5.5A2.5 2.5 0 0 1 3 18.5V16" }), _jsx("path", { d: "M9 9v1.5M15 9v1.5M12 9v4h-1M9.5 16c1.5 1.2 3.5 1.2 5 0" })] }));
const Backspace = () => (_jsxs("svg", { width: "30", height: "30", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "1.8", strokeLinejoin: "round", strokeLinecap: "round", "aria-hidden": "true", children: [_jsx("path", { d: "M21 5H9l-6 7 6 7h12a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1z" }), _jsx("path", { d: "M12.5 9.5l5 5M17.5 9.5l-5 5" })] }));
